import { BuildingIcon, ChevronsUpDown, UserIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/Drawer";
import { Skeleton } from "@/components/ui/skeleton";
import LogoutWarningDialog from "@/components/LogoutWarningDialog";
import { useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser } from "@/store/slices/user.slice";
import { formatPerson } from "@/utils/helper";
import { ProfileDrawer } from "./profile-drawer";
import {
  AddCompanyDialog,
  ENUM_ADD_COMPANY_QUERY_PARAM,
} from "./profile-drawer/add-company-dialog";

/**
 * `ProfileDrawer` owns its own top bar, scroll region and pinned footer, so the
 * drawer shell must not add body padding or a second scroll container.
 */
const DRAWER_BODY_CLASS = "flex min-h-0 flex-1 flex-col overflow-hidden p-0";

/** Hairline ring + deep shadow so the panel reads as floating, not welded on. */
const DRAWER_PANEL_CLASS = "ring-1 ring-border/70 shadow-2xl";

/** Avatar trigger with the profile drawer, logout dialog, add-company dialog. */
export const ProfileMenu = () => {
  const { t } = useTranslation("common");
  const [searchParams] = useSearchParams();
  const isAddCompanyOpen =
    searchParams.get(ENUM_ADD_COMPANY_QUERY_PARAM.name) ===
    ENUM_ADD_COMPANY_QUERY_PARAM.value;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLogoutWarningOpen, setIsLogoutWarningOpen] = useState(false);

  const { user } = useAppSelector(selectUser);
  const fullName = formatPerson(user);

  // Stable identities — `useProfileDrawer` memoises its navigate helper on these.
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);
  const requestSignOut = useCallback(() => setIsLogoutWarningOpen(true), []);

  // Close the profile drawer when the add-company dialog opens.
  useEffect(() => {
    if (isAddCompanyOpen) setIsDrawerOpen(false);
  }, [isAddCompanyOpen]);

  return (
    <>
      <Button
        variant="ghost"
        onClick={() => setIsDrawerOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={isDrawerOpen}
        aria-label={
          user
            ? t("header.account_menu_for", { name: fullName })
            : t("header.account_menu")
        }
        className="group h-9 justify-start gap-1.5 px-1.5 transition-colors aria-expanded:bg-muted"
      >
        {/* Spans, not divs/ps — a <button> only accepts phrasing content. */}
        <span className="relative block shrink-0">
          <Avatar className="bg-primary/10 border border-surface size-8 transition-shadow group-hover:ring-2 group-hover:ring-primary/30">
            <AvatarImage src={user?.profileImage ?? ""} alt="" />
            <AvatarFallback>
              <UserIcon size={16} aria-hidden />
            </AvatarFallback>
          </Avatar>
          <Avatar className="absolute -z-10 top-0.5 -left-1.5 size-6 bg-primary/10 border border-surface">
            <AvatarImage src={user?.company?.logoIcon ?? undefined} alt="" />
            <AvatarFallback>
              <BuildingIcon size={14} aria-hidden />
            </AvatarFallback>
          </Avatar>
        </span>
        <span className="hidden md:block w-36 leading-tight">
          {user ? (
            <>
              <span className="block truncate text-start text-sm capitalize">
                {fullName}
              </span>
              <span className="block truncate text-start text-xs text-muted-foreground">
                {user.roleName}
              </span>
            </>
          ) : (
            <span className="flex flex-col gap-1.5">
              <Skeleton as="span" className="h-3.5 w-24" />
              <Skeleton as="span" className="h-3 w-16" />
            </span>
          )}
        </span>
        <ChevronsUpDown
          aria-hidden
          className="text-muted-foreground size-4 shrink-0 transition-colors group-hover:text-primary"
        />
      </Button>

      <LogoutWarningDialog
        open={isLogoutWarningOpen}
        onConfirm={() => setIsLogoutWarningOpen(false)}
        onCancel={() => setIsLogoutWarningOpen(false)}
        onOpenChange={setIsLogoutWarningOpen}
      />
      <Drawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        size="md"
        srTitle={t("header.my_account")}
        styles={{ body: DRAWER_BODY_CLASS, drawerContent: DRAWER_PANEL_CLASS }}
      >
        <ProfileDrawer
          onClose={closeDrawer}
          onRequestSignOut={requestSignOut}
        />
      </Drawer>
      <AddCompanyDialog />
    </>
  );
};
